// Purpose: Atomically register manager entries and review public submissions.
// Dependencies: Firebase client singleton; manager permissions enforced by Rules.
// Behavior: Shared event counter serializes registration numbers; approval is idempotent.
import { collection, doc, getDocs, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export const entryCategoryId = (value) => /^\d+$/.test(String(value)) ? Number(value) : String(value);

// Keep a real, positive numeric category so existing ballots and result readers
// work without category setup. Never replace a category created by another manager.
export async function resolveEntryCategory(eventId, value) {
  const explicitDefault = value === "__default__";
  if (!explicitDefault && value !== undefined && value !== null && String(value) !== "" && String(value) !== "0") {
    return entryCategoryId(value);
  }
  const snapshot = await getDocs(collection(db, "events", eventId, "categories"));
  const categories = snapshot.docs.filter((item) =>
    !["0", "init", "_init", "_placeholder"].includes(item.id) &&
    !item.data().placeholder && String(item.data().name || "").toLowerCase() !== "init"
  );
  const defaultCategory = categories.find((item) => item.data().isDefault);
  if (defaultCategory) return entryCategoryId(defaultCategory.id);
  if (!explicitDefault && categories.some((item) => !item.data().isDefault)) throw new Error("Select a category.");
  const nextId = snapshot.docs.reduce((max, item) => Math.max(max, Number(item.id) || 0), 0) + 1;
  const categoryRef = doc(db, "events", eventId, "categories", String(nextId));
  await runTransaction(db, async (transaction) => {
    const existing = await transaction.get(categoryRef);
    if (existing.exists()) {
      if (!existing.data().isDefault) throw new Error("Select a category.");
      return;
    }
    transaction.set(categoryRef, { id: nextId, name: "", isDefault: true, sortOrder: nextId, eventId });
  });
  return nextId;
}

async function registrationFloor(eventId) {
  const entries = await getDocs(collection(db, "events", eventId, "cars"));
  return entries.docs.reduce((next, snapshot) => {
    const number = Number(snapshot.data().regcnt);
    return Number.isSafeInteger(number) ? Math.max(next, number + 1) : next;
  }, 1);
}

// Read the legacy maximum before the transaction; all current registration writers
// reserve through the event counter, so competing creates retry with a newer value.
export async function createManagerEntry(eventId, entry) {
  const catid = await resolveEntryCategory(eventId, entry.catid);
  const floor = await registrationFloor(eventId);
  const eventRef = doc(db, "events", eventId);
  const entryRef = doc(collection(db, "events", eventId, "cars"));
  const regcnt = await runTransaction(db, async (transaction) => {
    const event = await transaction.get(eventRef);
    if (!event.exists()) throw new Error("Event was not found.");
    const next = Math.max(floor, Number(event.data().nextEntryRegcnt) || 1);
    transaction.update(eventRef, { nextEntryRegcnt: next + 1 });
    transaction.set(entryRef, { ...entry, catid, regcnt: next, cid: entryRef.id });
    return next;
  });
  return { entryRef, regcnt };
}

export async function reviewPublicEntry(eventId, pendingEntryId, action, categoryId, transactionId) {
  if (!["approve", "reject"].includes(action)) throw new Error("Invalid review action.");
  if (transactionId !== undefined && (typeof transactionId !== "string" || transactionId.trim().length > 200)) throw new Error("Transaction ID must be 200 characters or fewer.");
  const catid = action === "approve" ? await resolveEntryCategory(eventId, categoryId) : null;
  const floor = action === "approve" ? await registrationFloor(eventId) : 1;
  const eventRef = doc(db, "events", eventId);
  const pendingRef = doc(db, "events", eventId, "pendingEntries", pendingEntryId);
  const entryRef = doc(collection(db, "events", eventId, "cars"));
  return runTransaction(db, async (transaction) => {
    const pending = await transaction.get(pendingRef);
    if (!pending.exists()) throw new Error("Submission was not found.");
    const submission = pending.data();
    if (submission.status === "approved" && action === "approve") {
      return { entryId: submission.approvedCarId, regcnt: submission.approvedRegCnt };
    }
    if (submission.status !== "pending") throw new Error("This submission is no longer pending review.");
    if (action === "reject") {
      transaction.update(pendingRef, { status: "rejected", rejectedAt: serverTimestamp(), updatedAt: serverTimestamp() });
      return {};
    }
    const event = await transaction.get(eventRef);
    if (!event.exists()) throw new Error("Event was not found.");
    const regcnt = Math.max(floor, Number(event.data().nextEntryRegcnt) || 1);
    const images = Array.isArray(submission.images) ? submission.images : [];
    transaction.set(entryRef, {
      eventId, cid: entryRef.id, regcnt, catid,
      description: submission.entryTitle || "", optionalDescription: submission.description || "",
      ownerFirst: submission.firstName || "", ownerLast: submission.lastName || "",
      email: submission.email || "", ownerEmail: submission.email || "", ownerId: submission.uid,
      facebook: submission.facebook || "", instagram: submission.instagram || "",
      images, hasImage: Boolean(images[0]?.url), imgUrl: images[0]?.url || "",
      publicEntrySourceId: pendingEntryId, publicEntryApproved: true, approved: true, pending: false,
      isAuctionItem: false, auctionItemId: "", createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    transaction.update(eventRef, { nextEntryRegcnt: regcnt + 1 });
    transaction.update(pendingRef, {
      status: "approved", approvedCarId: entryRef.id, approvedRegCnt: regcnt,
      transactionId: transactionId === undefined ? (submission.transactionId || "") : transactionId.trim(),
      approvedAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    return { entryId: entryRef.id, regcnt };
  });
}
